import { Component, computed, inject, input, OnDestroy, signal } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';

import { Transcription, TranscriptionRepository } from '@xmedical/core';
import { TRANSCRIBE_ERROR_HANDLER, TranscribeErrorHandler } from '../../services/error-handler.token';

/**
 * Audio playback component for transcription recordings
 *
 * Features:
 * - Play/pause audio from S3
 * - Skip forward/back 10 seconds
 * - Progress bar with seek
 * - Expandable transcription text view
 * - Lazy loading with signed URLs
 *
 * Usage:
 * ```html
 * <xmed-playback [transcription]="myTranscription" />
 * ```
 */
@Component({
  selector: 'xmed-playback',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule
],
  templateUrl: './playback.component.html',
  styleUrls: ['./playback.component.scss']
})
export class PlaybackComponent implements OnDestroy {
  private transcriptionRepo = inject(TranscriptionRepository);
  private errorHandler: TranscribeErrorHandler = inject(TRANSCRIBE_ERROR_HANDLER, { optional: true })
    ?? { error: (msg, err) => console.error(`[Transcribe] ${msg}`, err) };

  // Input
  transcription = input.required<Transcription>();

  // State
  loading = signal(false);
  playing = signal(false);
  showTranscript = signal(false);
  currentTime = signal(0);
  duration = signal(0);
  audioLoaded = signal(false);

  // Audio element
  private audioElement?: HTMLAudioElement;
  private signedUrl?: string;

  // Computed
  formattedCurrentTime = computed(() => this.formatTime(this.currentTime()));

  formattedDuration = computed(() => {
    // Use transcription duration if available, otherwise use audio element duration
    const durationSeconds = this.transcription().durationInSeconds || this.duration();
    return this.formatTime(durationSeconds * 1000);
  });

  progress = computed(() => {
    const durationSeconds = this.transcription().durationInSeconds || this.duration();
    if (durationSeconds <= 0) {
      return 0;
    }
    return (this.currentTime() / (durationSeconds * 1000)) * 100;
  });

  hasTranscript = computed(() => {
    const text = this.transcription().transcriptionText;
    return text && text.trim().length > 0;
  });

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Toggle play/pause
   * On first play, fetches signed URL and loads audio
   */
  async togglePlayback(): Promise<void> {
    if (this.loading()) {
      return;
    }

    if (!this.audioLoaded()) {
      await this.loadAudio();
    }

    if (this.audioElement) {
      if (this.playing()) {
        this.audioElement.pause();
        this.playing.set(false);
      } else {
        try {
          await this.audioElement.play();
          this.playing.set(true);
        } catch (e: unknown) {
          // AbortError is expected when pause() is called before play() resolves
          if (e instanceof DOMException && e.name === 'AbortError') {
            return;
          }
          throw e;
        }
      }
    }
  }

  /**
   * Skip forward 10 seconds
   */
  skipForward(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.currentTime = Math.min(
      this.audioElement.currentTime + 10,
      this.audioElement.duration || 0
    );
  }

  /**
   * Skip backward 10 seconds
   */
  skipBackward(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.currentTime = Math.max(
      this.audioElement.currentTime - 10,
      0
    );
  }

  /**
   * Seek to position based on progress bar click
   */
  seekTo(event: MouseEvent): void {
    if (!this.audioElement || !this.audioLoaded()) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const durationSeconds = this.transcription().durationInSeconds || this.duration();

    this.audioElement.currentTime = percentage * durationSeconds;
  }

  /**
   * Toggle transcript visibility
   */
  toggleTranscript(): void {
    this.showTranscript.set(!this.showTranscript());
  }

  /**
   * Load audio from S3 with signed URL
   */
  private async loadAudio(): Promise<void> {
    this.loading.set(true);

    try {
      // Get signed URL for the audio file (GET request, 5 min expiry)
      const path = this.transcription().path;
      this.signedUrl = await firstValueFrom(
        this.transcriptionRepo.getSignedUrl(path, 'GET', 300)
      );

      // Create audio element with source to specify MIME type
      this.audioElement = document.createElement('audio');
      const source = document.createElement('source');
      source.src = this.signedUrl;
      source.type = 'audio/webm;codecs=opus';
      this.audioElement.appendChild(source);

      // Also try ogg format as fallback
      const sourceOgg = document.createElement('source');
      sourceOgg.src = this.signedUrl;
      sourceOgg.type = 'audio/ogg;codecs=opus';
      this.audioElement.appendChild(sourceOgg);

      // Set up event listeners
      this.audioElement.ontimeupdate = () => {
        if (this.audioElement) {
          this.currentTime.set(this.audioElement.currentTime * 1000);
        }
      };

      this.audioElement.onloadedmetadata = () => {
        if (this.audioElement && isFinite(this.audioElement.duration)) {
          this.duration.set(this.audioElement.duration);
        }
      };

      this.audioElement.onended = () => {
        this.playing.set(false);
        this.currentTime.set(0);
        if (this.audioElement) {
          this.audioElement.currentTime = 0;
        }
      };

      this.audioElement.onerror = () => {
        this.loading.set(false);
        this.audioLoaded.set(false);
      };

      // Load the audio
      this.audioElement.load();

      // Wait for audio to be ready to play
      await new Promise<void>((resolve, reject) => {
        if (!this.audioElement) {
          reject(new Error('Audio element not created'));
          return;
        }

        let settled = false;
        const settle = (fn: () => void) => {
          if (!settled) {
            settled = true;
            fn();
          }
        };

        this.audioElement.oncanplay = () => settle(resolve);

        this.audioElement.onerror = () => {
          settle(() => reject(new Error('Failed to load audio')));
        };

        // Timeout fallback - if no events fire within 10s, check state
        setTimeout(() => {
          if (settled || !this.audioElement) {
            return;
          }
          // networkState 3 = NETWORK_NO_SOURCE (blocked/failed)
          if (this.audioElement.networkState === 3) {
            settle(() => reject(new Error('Audio blocked - check CloudFront CORS/referrer policy')));
          } else if (this.audioElement.readyState >= 2) {
            settle(resolve);
          }
        }, 10000);
      });

      this.audioLoaded.set(true);
      this.loading.set(false);

    } catch (error: unknown) {
      this.errorHandler.error('Failed to load audio', error);
      this.loading.set(false);
    }
  }

  /**
   * Format time in milliseconds to mm:ss
   */
  private formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Cleanup audio element
   */
  private cleanup(): void {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.ontimeupdate = null;
      this.audioElement.onloadedmetadata = null;
      this.audioElement.onended = null;
      this.audioElement.onerror = null;
      this.audioElement.oncanplay = null;
      this.audioElement = undefined;
    }
    this.signedUrl = undefined;
  }
}
